import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  Paper,
  Button,
  CircularProgress,
  Fade,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  LocalOffer as DiscountIcon,
  CleaningServices as CleaningIcon,
  Emergency as EmergencyIcon,
  FamilyRestroom as FamilyIcon,
  WhatsApp as WhatsAppIcon,
  CalendarMonth as CalendarIcon,
  People as PeopleIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { getUserPlan } from '../services/api_service';
import { useAlert } from '../AlertContext';
import { useNavigate } from 'react-router-dom';
import StatusCard from '../components/StatusCard';
import ModernCard from '../components/ModernCard';

const ModernHome = () => {
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState({});
  const session = JSON.parse(localStorage.getItem('session') || '{}');
  const { showAlert } = useAlert();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const benefits = [
    {
      title: '20% descuento',
      subtitle: 'en tratamientos dentales',
      description: 'Aplicable a todos los procedimientos odontológicos',
      icon: <DiscountIcon />,
      buttonText: 'Más información',
      action: () => openWhatsApp('tratamientos'),
      image: `${window.location.origin}/tratamiento.png`,
      color: '#FF9800',
    },
    {
      title: 'Higiene bucal profunda gratis',
      subtitle: 'cada 12 meses',
      description: 'Limpieza dental profesional',
      icon: <CleaningIcon />,
      buttonText: 'Agenda tu cita',
      action: () => navigate('/calendar'),
      image: `${window.location.origin}/descuento.png`,
      color: '#2196F3',
    },
    {
      title: 'Cita prioritaria',
      subtitle: 'en menos de 24 horas',
      description: 'Manejo prioritario en casos de dolor o sangrado',
      icon: <EmergencyIcon />,
      buttonText: 'Solicita tu cita',
      action: () => openWhatsApp('agenda'),
      image: `${window.location.origin}/emergencia.png`,
      color: '#F44336',
    },
    {
      title: '3 beneficiarios',
      subtitle: 'para tu familia',
      description: 'Comparte todos los beneficios con tu familia',
      icon: <FamilyIcon />,
      buttonText: 'Añadir beneficiarios',
      action: () => navigate('/beneficiaries'),
      image: `${window.location.origin}/beneficiarios.png`,
      color: '#4CAF50',
    },
  ];

  useEffect(() => {
    loadUserPlan();
  }, []);

  const loadUserPlan = async () => {
    try {
      const response = await getUserPlan(session.user_id);
      if (response?.result) {
        setPlan(response.result[0] || {});
      }
    } catch (error) {
      showAlert('Error al cargar datos del plan', 'error');
    } finally {
      setLoading(false);
    }
  };

  const openWhatsApp = (type) => {
    const phoneNumber = '573192746973';
    const messages = {
      tratamientos:
        'Hola, quiero más información sobre los descuentos para tratamientos incluidos en mi plan de afiliación',
      agenda: 'Hola, necesito solicitar una cita, tengo una emergencia',
      default: 'Hola, necesito ayuda.',
    };
    const message = messages[type] || messages.default;
    const url = `https://api.whatsapp.com/send?phone=${phoneNumber}&text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const handleRenewPlan = () => {
    navigate('/pay');
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress size={60} sx={{ color: '#94B754' }} thickness={4} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #F8FFFE 0%, #FFFFFF 100%)',
      }}
    >
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
        <Stack spacing={{ xs: 3, md: 4 }}>
          {/* Welcome Header */}
          <Fade in={true} timeout={600}>
            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <Typography
                variant={isMobile ? 'h5' : 'h4'}
                sx={{
                  fontWeight: 300,
                  color: '#666',
                  mb: 1,
                }}
              >
                Bienvenido de vuelta,
              </Typography>
              <Typography
                variant={isMobile ? 'h4' : 'h3'}
                sx={{
                  fontWeight: 'bold',
                  color: '#2C5530',
                  mb: 1,
                }}
              >
                {session?.user_name || 'Usuario'}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: '#666',
                  maxWidth: 600,
                }}
              >
                Gestiona tu plan de salud dental y accede a todos tus beneficios
              </Typography>
            </Box>
          </Fade>

          {/* Status Card */}
          <StatusCard
            plan={plan}
            session={session}
            onRenewClick={handleRenewPlan}
          />

          {/* Benefits Section */}
          <Box>
            <Fade in={true} timeout={800}>
              <Box sx={{ mb: 3, textAlign: { xs: 'center', md: 'left' } }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 'bold',
                    color: '#2C5530',
                    mb: 1,
                  }}
                >
                  ✨ Tus beneficios
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ maxWidth: 600 }}
                >
                  Descubre todo lo que tu plan incluye para ti y tu familia
                </Typography>
              </Box>
            </Fade>

            <Grid container spacing={{ xs: 2, md: 3 }}>
              {benefits.map((benefit, index) => (
                <Grid item xs={12} sm={6} lg={3} key={index}>
                  <ModernCard
                    title={benefit.title}
                    subtitle={benefit.subtitle}
                    description={benefit.description}
                    icon={benefit.icon}
                    image={benefit.image}
                    buttonText={benefit.buttonText}
                    onButtonClick={benefit.action}
                    color={benefit.color}
                    delay={index * 150}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Quick Actions */}
          <Fade in={true} timeout={1200}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 4,
                border: '1px solid #E0E0E0',
                background: 'linear-gradient(135deg, #F8FFF8 0%, #FFFFFF 100%)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <InfoIcon sx={{ color: '#94B754', mr: 1 }} />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 'bold',
                    color: '#2C5530',
                  }}
                >
                  Acciones rápidas
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Button
                    variant="outlined"
                    fullWidth
                    size="large"
                    startIcon={<WhatsAppIcon />}
                    onClick={() => openWhatsApp('default')}
                    sx={{
                      borderColor: '#25D366',
                      color: '#25D366',
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        borderColor: '#25D366',
                        backgroundColor: 'rgba(37, 211, 102, 0.1)',
                      },
                    }}
                  >
                    Contactar WhatsApp
                  </Button>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    variant="outlined"
                    fullWidth
                    size="large"
                    startIcon={<CalendarIcon />}
                    onClick={() => navigate('/calendar')}
                    sx={{
                      borderColor: '#94B754',
                      color: '#94B754',
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        borderColor: '#7DA647',
                        backgroundColor: 'rgba(148, 183, 84, 0.1)',
                      },
                    }}
                  >
                    Ver calendario
                  </Button>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    variant="outlined"
                    fullWidth
                    size="large"
                    startIcon={<PeopleIcon />}
                    onClick={() => navigate('/beneficiaries')}
                    sx={{
                      borderColor: '#94B754',
                      color: '#94B754',
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        borderColor: '#7DA647',
                        backgroundColor: 'rgba(148, 183, 84, 0.1)',
                      },
                    }}
                  >
                    Mis beneficiarios
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Fade>
        </Stack>
      </Container>
    </Box>
  );
};

export default ModernHome;
